import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ScrollToTop } from "./ScrollToTop";
import { Router } from "@app/router/Router";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { SessionCacheReset } from "@components/auth/SessionCacheReset";
import { ToastContainer } from "@components/notifications/ToastContainer";
import { useThemeStore } from "@stores/theme";
import { TENANT_THEME_LS_KEY } from "@lib/tenantPersistKeys";
import { useAuthRefresh } from "@hooks/useAuthRefresh";
import { I18nProvider } from "@lib/i18n/I18nProvider";
import { initDevPerformanceLogging } from "@app/performance/devPerf";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 2,
            gcTime: 1000 * 60 * 10,
            refetchOnWindowFocus: false,
            retry: (failureCount, err: unknown) => {
                const status = (err as { response?: { status?: number } })?.response?.status;
                // Client errors (bad id, no access, plan gate, billing lock) will not fix themselves.
                if (status && status >= 400 && status < 500 && status !== 429) return false;
                return failureCount < 2;
            },
        },
    },
});

export const App: React.FC = () => {
    useAuthRefresh();
    const theme = useThemeStore((s) => s.theme);
    const setTheme = useThemeStore((s) => s.setTheme);

    React.useEffect(() => {
        const stored = localStorage.getItem(TENANT_THEME_LS_KEY);
        let initialTheme: "light" | "dark" = "dark";
        if (stored) {
            try {
                initialTheme = JSON.parse(stored)?.state?.theme === "light" ? "light" : "dark";
            } catch {
                initialTheme = "dark";
            }
        }
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(initialTheme);
        if (theme !== initialTheme) setTheme(initialTheme);
    }, []);

    React.useEffect(() => {
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(theme);
    }, [theme]);

    React.useEffect(() => {
        if (import.meta.env.DEV) initDevPerformanceLogging();
    }, []);

    return (
        <I18nProvider>
            <QueryClientProvider client={queryClient}>
                <SessionCacheReset />
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <ScrollToTop />
                    <ErrorBoundary>
                        <Router />
                    </ErrorBoundary>
                </BrowserRouter>
                <ToastContainer />
            </QueryClientProvider>
        </I18nProvider>
    );
};
